/**
 * What a metric MEANS — ported from the dispatch dashboard's
 * `lib/domain/dashboard/` and deliberately kept a port rather than a rewrite.
 *
 * Every value in here is a wire code the other app already writes. A label
 * invented on this side would give one metric two names depending on which app
 * an operator opened it in, and a currency subcategory read as a count would
 * put dollars on a chart axis labelled "jobs". So this file is the one place
 * that knows dispatch's vocabulary, and the rule for editing it is: change it
 * when the dashboard's own domain files change, and not otherwise.
 *
 * Three of those mappings are load-bearing and are what the tests pin:
 *   - which subcategories are MONEY (`valueKindOf`), because the formatter and
 *     the axis both read it;
 *   - which read their figure from the API's `sum` (`usesSum`). The one money
 *     subcategory missing from that set is `JOBS_TOTAL`: its dollars are on
 *     each record, and the point's `total` is the job count. Reading that
 *     count draws it as money;
 *   - which are SNAPSHOTS (`isSnapshotSubcategory`) — all-time pending figures
 *     that carry no window at all, so the page's frame does not apply to them
 *     and must not be shown as though it did.
 */

export type MetricType = 'barChart' | 'lineChart' | 'kpi';
export type ValueKind = 'count' | 'currency';

export interface CategorySpec {
  id: string;
  label: string;
}

/** dispatch's categories, in the order its own builder lists them. */
export const CATEGORIES: CategorySpec[] = [
  { id: 'jobs', label: 'Jobs' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'customers', label: 'Customers' },
  { id: 'affiliates', label: 'Affiliates' },
  { id: 'drivers', label: 'Chauffeurs' },
  { id: 'quotes', label: 'Quotes' },
  { id: 'dispatch', label: 'Dispatch' },
  { id: 'payables', label: 'Payables' },
  { id: 'paylog', label: 'Paylog' },
];

export const categoryLabel = (id: string): string =>
  CATEGORIES.find((category) => category.id === id.toLowerCase())?.label || id;

/**
 * The `dispatch` category is counted from the job feed rather than read from
 * the metrics API — a different source, a different shape, and a set of
 * matching rules (statuses, farm-out, payment, attention) that live with the
 * feed. It is named here so a card of that category can say what it is waiting
 * for instead of drawing a zero.
 */
export const isFeedCategory = (id: string): boolean => id.toLowerCase() === 'dispatch';

export interface SubcategorySpec {
  code: string;
  label: string;
  category: string;
}

export const SUBCATEGORIES: SubcategorySpec[] = [
  // Invoices
  { code: 'NEW_INVOICES', label: 'New Invoices', category: 'invoices' },
  { code: 'CLOSED_INVOICES', label: 'Closed Invoices', category: 'invoices' },
  { code: 'INVOICE_FINALIZED', label: 'Invoice Finalized', category: 'invoices' },
  { code: 'INVOICE_TOTAL', label: 'Invoice Total', category: 'invoices' },
  { code: 'INVOICE_DUE', label: 'Invoice Due', category: 'invoices' },
  { code: 'INVOICE_RECEIVED', label: 'Invoice Received', category: 'invoices' },

  // Customers / affiliates / chauffeurs
  { code: 'NEW_CUSTOMER', label: 'New Customer', category: 'customers' },
  { code: 'NEW_AFFILIATE', label: 'New Affiliate', category: 'affiliates' },
  { code: 'DRIVER_FORM_SUBMISSIONS', label: 'Chauffeur Form Submissions', category: 'drivers' },
  { code: 'APPROVED_DRIVERS', label: 'Approved Chauffeurs', category: 'drivers' },
  { code: 'PENDING_DRIVERS', label: 'Pending Chauffeurs', category: 'drivers' },

  // Quotes
  { code: 'ACCEPTED_QUOTES', label: 'Accepted Quotes', category: 'quotes' },
  { code: 'NEW_QUOTES', label: 'New Quotes', category: 'quotes' },
  { code: 'PENDING_QUOTES', label: 'Pending Quotes', category: 'quotes' },
  { code: 'DECLINED_QUOTES', label: 'Declined Quotes', category: 'quotes' },
  { code: 'QUOTES_TOTAL', label: 'Quotes Total', category: 'quotes' },
  { code: 'QUOTES_ACCEPTED_TOTAL', label: 'Quotes Accepted Total', category: 'quotes' },
  { code: 'QUOTES_DECLINED_TOTAL', label: 'Quotes Declined Total', category: 'quotes' },

  // Jobs
  { code: 'NEW_JOBS', label: 'New Jobs', category: 'jobs' },
  { code: 'PENDING_JOBS', label: 'Pending Jobs', category: 'jobs' },
  { code: 'REJECTED_JOBS', label: 'Rejected Jobs', category: 'jobs' },
  { code: 'CANCELLED_JOBS', label: 'Cancelled Jobs', category: 'jobs' },
  { code: 'DONE_JOBS', label: 'Done Jobs', category: 'jobs' },
  { code: 'NO_SHOW_JOBS', label: 'No Show Jobs', category: 'jobs' },
  { code: 'JOBS_TOTAL', label: 'Jobs Total', category: 'jobs' },
  { code: 'HOURLY_JOBS', label: 'Hourly Jobs', category: 'jobs' },
  { code: 'ONE_WAY_JOBS', label: 'One Way Jobs', category: 'jobs' },
  { code: 'GUEST_JOBS', label: 'Guest Jobs', category: 'jobs' },
  { code: 'CUSTOMER_JOBS', label: 'Customer Jobs', category: 'jobs' },
  { code: 'AFFILIATE_JOBS', label: 'Affiliate Jobs', category: 'jobs' },
  { code: 'FARM_IN_JOBS', label: 'Farm In Jobs', category: 'jobs' },
  { code: 'IN_HOUSE_JOBS', label: 'In House Jobs', category: 'jobs' },

  // Dispatch — counted from the job feed, not this API.
  { code: 'DISPATCH_PENDING', label: 'Pending', category: 'dispatch' },
  { code: 'DISPATCH_CONFIRMED', label: 'Confirmed', category: 'dispatch' },
  { code: 'DISPATCH_DRIVER_ASSIGNED', label: 'Chauffeur Assigned', category: 'dispatch' },
  { code: 'DISPATCH_IN_PROGRESS', label: 'In Progress', category: 'dispatch' },
  { code: 'DISPATCH_DONE', label: 'Done', category: 'dispatch' },
  { code: 'DISPATCH_NO_SHOW', label: 'No Show', category: 'dispatch' },
  { code: 'DISPATCH_CANCELLED', label: 'Cancelled', category: 'dispatch' },
  { code: 'DISPATCH_AWAITING_DRIVER', label: 'Awaiting Chauffeur', category: 'dispatch' },
  { code: 'DISPATCH_FARM_OUT_UNASSIGNED', label: 'Farm Out Unassigned', category: 'dispatch' },
  { code: 'DISPATCH_FARM_OUT_REJECTED', label: 'Farm Out Rejected', category: 'dispatch' },
  { code: 'DISPATCH_SETTLED_COMPLETE', label: 'Settled / Complete', category: 'dispatch' },
  { code: 'DISPATCH_GUEST_JOBS', label: 'Guest Jobs', category: 'dispatch' },
  { code: 'DISPATCH_CUSTOMER_JOBS', label: 'Customer Jobs', category: 'dispatch' },
  { code: 'DISPATCH_AFFILIATE_FARM_IN', label: 'Affiliate Farm-In Jobs', category: 'dispatch' },
  { code: 'DISPATCH_ADMIN_CREATED', label: 'Admin Created Jobs', category: 'dispatch' },
  { code: 'DISPATCH_FARM_OUT_GNET', label: 'Farmout with GNet', category: 'dispatch' },
  { code: 'DISPATCH_FARM_OUT_LOCAL', label: 'Farmout Locally', category: 'dispatch' },
  { code: 'DISPATCH_FARM_OUT', label: 'Farmout', category: 'dispatch' },
  { code: 'DISPATCH_DUAL_DRIVER', label: 'Dual Chauffeur', category: 'dispatch' },
  { code: 'DISPATCH_PAYMENT_DUE', label: 'Payment Due', category: 'dispatch' },
  { code: 'DISPATCH_PARTIALLY_PAID', label: 'Partially Paid', category: 'dispatch' },
  { code: 'DISPATCH_FULLY_PAID', label: 'Fully Paid', category: 'dispatch' },
  { code: 'DISPATCH_MULTI_STOP', label: 'Multi-stop', category: 'dispatch' },
  { code: 'DISPATCH_FLIGHT_JOBS', label: 'Flight Jobs', category: 'dispatch' },
  { code: 'DISPATCH_NEED_ATTENTION', label: 'Need Attention', category: 'dispatch' },
  { code: 'DISPATCH_DELAYED', label: 'Delayed', category: 'dispatch' },

  // Payables / paylog — money, and cleared payables drop out of the feed.
  { code: 'PAYABLES_TOTAL', label: 'Payables Total', category: 'payables' },
  { code: 'PAYABLES_BY_PICKUP_DATE', label: 'Payables by Pickup Date', category: 'payables' },
  { code: 'PAYABLES_CHAUFFEUR', label: 'Payables for Chauffeurs', category: 'payables' },
  { code: 'PAYABLES_AFFILIATE', label: 'Payables for Affiliates', category: 'payables' },
  { code: 'PAYLOG_TOTAL', label: 'Total', category: 'paylog' },
  { code: 'PAYLOG_CHAUFFEUR', label: 'Chauffeur', category: 'paylog' },
  { code: 'PAYLOG_AFFILIATE', label: 'Affiliate', category: 'paylog' },
];

const BY_CODE = new Map(SUBCATEGORIES.map((sub) => [sub.code, sub]));

/**
 * Resolve a stored code, in order of decreasing confidence: exact, then
 * case-insensitive, then the snake-normalized form, then the label.
 *
 * The label pass comes LAST for the reason the Dart version says so: two
 * categories both have a `Guest Jobs` and a `Customer Jobs`, so a label pass
 * that ran first would let one entry's label shadow another entry's code.
 */
export const subcategoryOf = (value: string): SubcategorySpec | undefined => {
  const trimmed = String(value || '').trim();
  const exact = BY_CODE.get(trimmed);
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  const insensitive = SUBCATEGORIES.find((sub) => sub.code.toLowerCase() === lower);
  if (insensitive) return insensitive;
  const snake = trimmed.replace(/\s+/g, '_').replace(/-/g, '_').toUpperCase();
  const snaked = BY_CODE.get(snake);
  if (snaked) return snaked;
  return SUBCATEGORIES.find((sub) => sub.label.toLowerCase() === lower);
};

export const subcategoryLabel = (value: string): string => subcategoryOf(value)?.label ?? value;

export const subcategoriesFor = (category: string): SubcategorySpec[] =>
  SUBCATEGORIES.filter((sub) => sub.category === category.toLowerCase());

/* ---- what a number IS ----------------------------------------------------- */

const CURRENCY = new Set([
  'JOBS_TOTAL',
  'QUOTES_TOTAL',
  'QUOTES_ACCEPTED_TOTAL',
  'QUOTES_DECLINED_TOTAL',
  'INVOICE_RECEIVED',
  'INVOICE_DUE',
  'INVOICE_TOTAL',
  'PAYABLES_TOTAL',
  'PAYABLES_BY_PICKUP_DATE',
  'PAYABLES_CHAUFFEUR',
  'PAYABLES_AFFILIATE',
  'PAYLOG_TOTAL',
  'PAYLOG_CHAUFFEUR',
  'PAYLOG_AFFILIATE',
]);

/**
 * Subcategories whose figure comes from the API's `sum` field.
 *
 * `JOBS_TOTAL` is money and is not in here. The point's `total` is the record
 * count for every subcategory, and Jobs Total never writes `sum` — its dollars
 * live on each record (`data[].total`), which is what dispatch sums. Reading
 * `point.total` for it draws the number of jobs as a dollar figure.
 */
const API_SUM = new Set([
  'QUOTES_TOTAL',
  'QUOTES_ACCEPTED_TOTAL',
  'QUOTES_DECLINED_TOTAL',
  'INVOICE_RECEIVED',
  'INVOICE_DUE',
  'INVOICE_TOTAL',
  'PAYABLES_TOTAL',
  'PAYABLES_BY_PICKUP_DATE',
  'PAYABLES_CHAUFFEUR',
  'PAYABLES_AFFILIATE',
  'PAYLOG_TOTAL',
  'PAYLOG_CHAUFFEUR',
  'PAYLOG_AFFILIATE',
]);

const normalizeCode = (value: string): string =>
  subcategoryOf(value)?.code ??
  String(value || '')
    .trim()
    .replace(/-/g, '_')
    .replace(/\s/g, '_')
    .toUpperCase();

export const valueKindOf = (code: string): ValueKind =>
  CURRENCY.has(normalizeCode(code)) ? 'currency' : 'count';

export const usesSum = (code: string): boolean => API_SUM.has(normalizeCode(code));

/**
 * The customers and affiliates handlers answer under a different code than the
 * one a card stores. Dispatch draws the series it got back; a lookup that
 * requires the stored code drops it and the card is empty.
 */
const SERIES_ALIAS: Record<string, string> = {
  CUSTOMERS: 'NEW_CUSTOMER',
  AFFILIATES: 'NEW_AFFILIATE',
};

const canonicalSeriesCode = (value: string): string => {
  const upper = String(value || '')
    .trim()
    .toUpperCase();
  return SERIES_ALIAS[upper] ?? upper;
};

/** True when a stored subcategory and an answered series are the same figure. */
export const seriesMatches = (asked: string, answered: string): boolean =>
  canonicalSeriesCode(asked) === canonicalSeriesCode(answered);

/** The shared kind when every subcategory agrees, else null — a mixed card has no one axis. */
export const sharedValueKind = (codes: string[]): ValueKind | null => {
  let kind: ValueKind | null = null;
  for (const code of codes) {
    const next = valueKindOf(code);
    if (kind === null) kind = next;
    else if (kind !== next) return null;
  }
  return kind;
};

/**
 * All-time figures that carry no window.
 *
 * These answer `period: "all"` and mean "how many are outstanding right now",
 * so the page's date frame does not narrow them and the card must not imply it
 * does. The percentage change goes with the window: there is no previous
 * period to compare a running total against.
 */
const SNAPSHOT = new Set(['PENDING_JOBS', 'PENDING_QUOTES', 'PENDING_DRIVERS', 'PAYABLES_TOTAL']);

export const SNAPSHOT_GROUP_BY = 'all';

export const isSnapshotSubcategory = (code: string): boolean => SNAPSHOT.has(normalizeCode(code));

/** A metric is a snapshot when it draws exactly one, which is how dispatch stores them. */
export const isSnapshotMetric = (subCategory: string[]): boolean =>
  subCategory.length === 1 && isSnapshotSubcategory(subCategory[0]);

/* ---- type and name -------------------------------------------------------- */

export const metricTypeOf = (value: string): MetricType => {
  const lower = String(value || '')
    .trim()
    .toLowerCase();
  if (lower === 'kpi') return 'kpi';
  if (lower === 'linechart') return 'lineChart';
  return 'barChart';
};

export const isChartType = (type: MetricType): boolean => type !== 'kpi';

/** Title-cases each word, exactly as the dashboard renders a saved name. */
export const displayName = (name: string): string => {
  const trimmed = String(name || '').trim();
  if (!trimmed) return trimmed;
  return trimmed
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ');
};

/* ---- KPI grouping --------------------------------------------------------- */

/**
 * On a KPI metric, `arrangeBy` is not an arrangement — it is a free-text group
 * header, stored verbatim by dispatch. Three values are presets with a fixed
 * place on the page; anything else is a heading somebody typed.
 *
 * It stays a string rather than becoming an enum on this side because the field
 * is free text upstream: a tenant who typed "Morning numbers" has a group, and
 * an enum would silently file it under the default.
 */
export const KPI_PRESETS = {
  kpiRow: 'KPI Row',
  dispatchRow: 'Dispatch Row',
  belowCharts: 'Below Charts',
} as const;

export type KpiPlacement = 'above' | 'below';

export const normalizeKpiHeader = (header: string | null | undefined): string => {
  const trimmed = String(header || '').trim();
  return trimmed || KPI_PRESETS.kpiRow;
};

const presetOfHeader = (header: string): string | null => {
  const lower = normalizeKpiHeader(header).toLowerCase();
  for (const value of Object.values(KPI_PRESETS)) {
    if (value.toLowerCase() === lower) return value;
  }
  return null;
};

/** A typed heading sits above the charts unless it is the Below Charts preset. */
export const kpiPlacement = (header: string | null | undefined): KpiPlacement =>
  presetOfHeader(normalizeKpiHeader(header)) === KPI_PRESETS.belowCharts ? 'below' : 'above';

/** KPI Row, then Dispatch Row, then anything typed — alphabetically among themselves. */
export const kpiHeaderRank = (header: string): number => {
  switch (presetOfHeader(header)) {
    case KPI_PRESETS.kpiRow:
      return 0;
    case KPI_PRESETS.dispatchRow:
      return 1;
    default:
      return 3;
  }
};

/**
 * What to print over a KPI group, or null for no heading at all.
 *
 * The default group prints nothing: it is the band of headline figures at the
 * top of the page, and "KPI Row" over them is a label describing the widget
 * rather than the numbers.
 */
export const kpiHeaderLabel = (header: string): string | null => {
  switch (presetOfHeader(header)) {
    case KPI_PRESETS.kpiRow:
      return null;
    case KPI_PRESETS.dispatchRow:
      return 'Dispatch';
    case KPI_PRESETS.belowCharts:
      return KPI_PRESETS.belowCharts;
    default:
      return normalizeKpiHeader(header);
  }
};
